import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  subscribeToClients,
  subscribeToAllObjects,
  subscribeToAllInspections,
  getReporterUsers,
} from '../utils/storage';
import { computeDeliveryDate } from '../utils/inspectionStatus';
import { readTimelineCache, writeTimelineCache } from '../utils/timelineCache';
import { useAuth } from '../contexts/AuthContext';
import './InspectionStatus.css';
import './Dashboard.css';

// ── Constants ───────────────────────────────────────────────────
const DAY_W      = 48;   // px per day column
const ROW_H      = 48;   // px per object row
const LABEL_W    = 220;  // px for the sticky label column
const MIN_WEEKS  = 3;    // never show fewer than 3 complete weeks
const WEEK_HDR_H = 26;   // height of week-number header row
const DAY_HDR_H  = 48;   // height of day header (square)

const DAY_ABBR   = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Maps status name → CSS variable key
const STATUS_KEY = {
  'Planning':     'planning',
  'Execution':    'execution',
  'Handover':     'handover',
  'Reporting':    'reporting',
  'Delivery':     'delivery',
  'Delayed':      'delayed',
  'Missing data': 'missing',
};

function statusBg(s) { return `var(--status-${STATUS_KEY[s] || 'planning'}-bg)`; }

// ── Date helpers ────────────────────────────────────────────────
function getMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d;
}

function getWeekNumber(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - (d.getDay() + 6) % 7);
  const w1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - w1) / 86400000 - 3 + (w1.getDay() + 6) % 7) / 7);
}

function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }

// Format a 'YYYY-MM-DD' date as "01 Sep 2026"; '' when absent/invalid.
function fmtDue(ymd) {
  if (!ymd) return '';
  const d = new Date(ymd + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')} ${MONTH_ABBR[d.getMonth()]} ${d.getFullYear()}`;
}

// Inspection period as on the object page's Assessment card (en-GB short
// month): "07 Oct 2026 → 08 Oct 2026", or a single date when it's one day.
function fmtPeriodDate(ymd) {
  const d = new Date(ymd + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtPeriod(start, end) {
  if (!start) return '';
  const s = fmtPeriodDate(start);
  return end && end !== start ? `${s} → ${fmtPeriodDate(end)}` : s;
}

function plural(n, word) { return `${n} ${word}${n === 1 ? '' : 's'}`; }

// Relative due text: "Due in 3 days (04 Oct 2026)", "Due tomorrow (…)",
// "Due today (…)", "Due yesterday (…)", "Due 5 days ago (…)".
function fmtDueRelative(ymd, today) {
  const date = fmtDue(ymd);
  if (!date) return '';
  // Math.round absorbs the ±1h DST shift between two local midnights.
  const diff = Math.round((new Date(ymd + 'T00:00:00') - today) / 86400000);
  if (diff === 0)  return `Due today (${date})`;
  if (diff === 1)  return `Due tomorrow (${date})`;
  if (diff === -1) return `Due yesterday (${date})`;
  if (diff > 0)    return `Due in ${plural(diff, 'day')} (${date})`;
  return `Due ${plural(-diff, 'day')} ago (${date})`;
}

// Text on the single "all inspections delivered" timeline bar. With an L3
// inspector involved the reports go to them first; otherwise straight to the
// account manager for the client.
const READY_TEXT_L3     = 'Reports ready for L3 inspector. Notify the project coordinator.';
const READY_TEXT_CLIENT = 'Project ready for delivery, notify the account manager.';
const READY_COLLAPSE_MS = 60 * 1000;  // expanded rows fold back after 60 s

// ── Main Component ───────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const userEmail = userProfile?.email || '';

  // Seed from the session cache so a revisit paints the final timeline
  // instantly (no cold-load flash). Absent / invalidated cache → cold load
  // with the "Loading Timeline" → skeleton → final sequence handled below.
  const cachedRef = useRef(readTimelineCache());
  const cached = cachedRef.current;

  const [clients,     setClients]     = useState(cached?.clients || []);
  const [objects,     setObjects]     = useState(cached?.objects || []);
  const [inspections, setInspections] = useState(cached?.inspections || []);
  // Per-source "first fetch has arrived" flags — distinguish "still loading"
  // from "genuinely empty". Seeded true when we already have a cached snapshot.
  const [objectsLoaded,     setObjectsLoaded]     = useState(!!cached);
  const [clientsLoaded,     setClientsLoaded]     = useState(!!cached);
  const [inspectionsLoaded, setInspectionsLoaded] = useState(!!cached);
  const [weekOffset,  setWeekOffset]  = useState(0);
  const [tooltip,     setTooltip]     = useState(null); // { text, sub, x, y }
  const tooltipRef = useRef(null);

  // Measure the available width so the calendar can grow by whole weeks on
  // wider screens (cell sizes stay fixed — we only add columns when a full
  // week fits). rootRef is on the full-width dashboard container.
  const rootRef = useRef(null);
  const [availWidth, setAvailWidth] = useState(0);
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const update = () => setAvailWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const FRAME_MARGIN = 60;   // 30px each side (.tl-wrap horizontal margin)
  const FRAME_BORDER = 2;
  const FRAME_GAP    = 25;    // extra frame width beyond the calendar, so the
                             // last column's border sits off the frame border.
                             // Applied as width (not padding) and left as slack
                             // on the right, so rounding never triggers a scroll.
  const FRAME_CHROME = FRAME_BORDER + FRAME_GAP;
  const weeksToShow = useMemo(() => {
    if (!availWidth) return MIN_WEEKS;
    const usable = availWidth - FRAME_MARGIN - FRAME_CHROME - LABEL_W;
    return Math.max(MIN_WEEKS, Math.floor(usable / (7 * DAY_W)));
  }, [availWidth, FRAME_CHROME]);
  const VIEW_DAYS = weeksToShow * 7;

  const fullyLoaded = objectsLoaded && clientsLoaded && inspectionsLoaded;

  useEffect(() => {
    const u1 = subscribeToClients((d) => { setClients(d); setClientsLoaded(true); });
    const u2 = subscribeToAllObjects((d) => { setObjects(d); setObjectsLoaded(true); });
    const u3 = subscribeToAllInspections((d) => { setInspections(d); setInspectionsLoaded(true); });
    return () => { u1(); u2(); u3(); };
  }, []);

  // Refresh the session cache whenever a full snapshot is in hand (initial
  // load or a later background poll), so the next visit stays instant.
  useEffect(() => {
    if (fullyLoaded) writeTimelineCache({ clients, objects, inspections });
  }, [fullyLoaded, clients, objects, inspections]);

  // Today with time zeroed
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // View window: Monday of (current week + offset)
  const viewStart = useMemo(() => {
    const d = getMonday(today);
    d.setDate(d.getDate() + weekOffset * 7);
    return d;
  }, [today, weekOffset]);

  // Right edge of the current view (viewStart + VIEW_DAYS).
  // Used as a fallback bar end-date for "No Deadline" objects so their bar
  // always stretches to the current view boundary and updates as you navigate.
  const viewEndDate = useMemo(() => {
    const d = new Date(viewStart);
    d.setDate(d.getDate() + VIEW_DAYS);
    return d;
  }, [viewStart, VIEW_DAYS]);

  // Lookup maps
  const clientMap = useMemo(
    () => Object.fromEntries(clients.map(c => [c.id, c])),
    [clients]
  );

  const objectMap = useMemo(
    () => Object.fromEntries(objects.map(o => [o.id, o])),
    [objects]
  );

  // ── Tabs: "projects" | "timeline" (default on load) ──
  const [tab, setTab] = useState('timeline');

  // ── Whose projects: signed-in user by default, a reporter/operator picked
  // from the tab's dropdown, or unassigned (null = "My Projects"). ──
  const [viewUser,     setViewUser]     = useState(null); // { email, name } | { unassigned: true }
  const [peopleOpen,   setPeopleOpen]   = useState(false);
  const [people,       setPeople]       = useState([]);   // reporter/operator users
  const peopleMenuRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    getReporterUsers().then((list) => { if (!cancelled) setPeople(list); });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (!peopleOpen) return undefined;
    const onDown = (e) => {
      if (peopleMenuRef.current && !peopleMenuRef.current.contains(e.target)) setPeopleOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [peopleOpen]);
  function pickPerson(u) {
    // Clicking the person already shown (or yourself) returns to My Projects.
    const email = u.email.toLowerCase();
    const isBack = email === (viewUser?.email || '').toLowerCase() || email === userEmail.toLowerCase();
    setViewUser(isBack ? null : { email: u.email, name: u.name });
    setPeopleOpen(false);
  }
  function pickUnassigned() {
    setViewUser(v => (v?.unassigned ? null : { unassigned: true }));
    setPeopleOpen(false);
  }
  // The first tab's text doubles as the dropdown trigger once it's the
  // active tab; from the timeline tab it just switches back.
  function onProjectsTabClick() {
    if (tab !== 'projects') { setTab('projects'); return; }
    setPeopleOpen(o => !o);
  }
  const projectsTitle = viewUser?.unassigned
    ? 'Unassigned Projects'
    : viewUser ? `${viewUser.name}'s Projects` : 'My Projects';

  // ── Project cards: inspections explicitly assigned to the viewed user ──
  // Matched by the assignee's email (assignedReporter.email), so it's robust
  // against duplicate display names. "Unassigned" = no assignee, limited to
  // inspections whose (non-archived) object is loaded.
  const myProjects = useMemo(() => {
    const myEmail = (viewUser?.email || userEmail || '').toLowerCase();
    const matches = viewUser?.unassigned
      ? (i => !i.formData?.assignedReporter?.email && !!objectMap[i.objectId])
      : (i => (i.formData?.assignedReporter?.email || '').toLowerCase() === myEmail);
    if (!viewUser?.unassigned && !myEmail) return [];
    return inspections
      .filter(matches)
      .map(i => {
        const obj = objectMap[i.objectId];
        const client = obj ? clientMap[obj.clientId] : null;
        const due = obj
          ? computeDeliveryDate(obj.generalInfo?.startDate, obj.settings?.reportingPeriod || '2 Weeks')
          : null;
        return {
          id: i.id,
          objectId: i.objectId,
          clientId: obj?.clientId,
          code: i.type || i.name || 'Inspection',
          status: i.effectiveStatus,
          objTag: obj?.objectTag || '—',
          period: fmtPeriod(obj?.generalInfo?.startDate, obj?.generalInfo?.endDate),
          site: client?.name || '',
          due,
        };
      })
      .sort((a, b) => a.objTag.localeCompare(b.objTag));
  }, [inspections, objectMap, clientMap, userEmail, viewUser]);

  const inspsByObject = useMemo(() => {
    const m = {};
    for (const insp of inspections) {
      if (!m[insp.objectId]) m[insp.objectId] = [];
      m[insp.objectId].push(insp);
    }
    return m;
  }, [inspections]);

  // All objects that have a start date: latest start on top (newest projects
  // first); same start date → test site name, then Object ID (numeric-aware).
  const filteredObjects = useMemo(() => {
    const cmp = (a, b) => (a || '').localeCompare(b || '', undefined, { numeric: true, sensitivity: 'base' });
    return objects
      .filter(o => o.generalInfo?.startDate)
      .sort((a, b) =>
        b.generalInfo.startDate.localeCompare(a.generalInfo.startDate)
        || cmp(clientMap[a.clientId]?.name, clientMap[b.clientId]?.name)
        || cmp(a.objectTag, b.objectTag));
  }, [objects, clientMap]);

  // Day offset from viewStart (negative = before window, ≥ VIEW_DAYS = after window)
  function dayOff(date) {
    return Math.round((date - viewStart) / 86400000);
  }

  // ── Header data ─────────────────────────────────────────────
  const weekHeaders = Array.from({ length: weeksToShow }, (_, i) => i * 7).map(off => {
    const firstDay = new Date(viewStart);
    firstDay.setDate(viewStart.getDate() + off);
    const lastDay  = new Date(viewStart);
    lastDay.setDate(viewStart.getDate() + off + 6);
    // If the 7-day window spans two calendar months, show "Mmm – Mmm"
    const m1 = MONTH_ABBR[firstDay.getMonth()];
    const m2 = MONTH_ABBR[lastDay.getMonth()];
    return {
      off,
      weekNum:    getWeekNumber(firstDay),
      monthLabel: firstDay.getMonth() !== lastDay.getMonth() ? `${m1} – ${m2}` : m1,
    };
  });

  const dayHeaders = Array.from({ length: VIEW_DAYS }, (_, i) => {
    const d = new Date(viewStart);
    d.setDate(viewStart.getDate() + i);
    const dow = d.getDay(); // 0=Sun
    return {
      i,
      dateNum:   d.getDate(),
      dayAbbr:   DAY_ABBR[dow === 0 ? 6 : dow - 1],
      isWeekend: dow === 0 || dow === 6,
      isToday:   d.getTime() === today.getTime(),
    };
  });

  const totalW = LABEL_W + VIEW_DAYS * DAY_W;

  const weekLines = Array.from({ length: weeksToShow - 1 }, (_, w) => (
    <div key={w} className="tl-vline" style={{ left: LABEL_W + (w + 1) * 7 * DAY_W - 1 }} />
  ));

  // ── "Top" button — jump to the week of the earliest object start date ──
  function scrollToEarliest() {
    if (filteredObjects.length === 0) return;
    const earliest = filteredObjects[filteredObjects.length - 1]; // sorted latest-first
    const startDate = new Date(earliest.generalInfo.startDate + 'T00:00:00');
    const targetMonday = getMonday(startDate);
    const baseMonday   = getMonday(today);
    const offsetWeeks  = Math.round((targetMonday - baseMonday) / (7 * 86400000));
    setWeekOffset(offsetWeeks);
  }

  // ── All-delivered rows: single "ready" bar ↔ real segments ──
  // Rows whose inspections are all in Delivery show one full-width bar with a
  // hand-off message. Clicking it expands the row back to its real segments
  // and snaps the view to the row's start week; it folds back after 60 s
  // (or on reload, since this is plain component state).
  const [expandedReady, setExpandedReady] = useState({}); // { [objectId]: true }
  const readyTimers = useRef({});
  useEffect(() => () => Object.values(readyTimers.current).forEach(clearTimeout), []);
  function expandReadyRow(obj) {
    clearTimeout(readyTimers.current[obj.id]);
    setExpandedReady(m => ({ ...m, [obj.id]: true }));
    readyTimers.current[obj.id] = setTimeout(() => {
      delete readyTimers.current[obj.id];
      setExpandedReady(m => {
        const { [obj.id]: _, ...rest } = m;
        return rest;
      });
    }, READY_COLLAPSE_MS);
    const targetMonday = getMonday(new Date(obj.generalInfo.startDate + 'T00:00:00'));
    setWeekOffset(Math.round((targetMonday - getMonday(today)) / (7 * 86400000)));
  }

  // ── Tooltip handlers ─────────────────────────────────────────
  function onSegEnter(e, text, sub) { setTooltip({ text, sub, x: e.clientX, y: e.clientY }); }
  function onSegMove(e)        { setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null); }
  function onSegLeave()        { setTooltip(null); }

  // ── Smart tooltip positioning ─────────────────────────────────
  // Runs synchronously after every render (before browser paint) so there
  // is no visible flicker.  The tooltip div starts invisible at (0,0);
  // this effect measures its actual width and repositions it:
  //   • Default: 12 px to the RIGHT of the cursor
  //   • Flipped:  12 px to the LEFT  if the right-side position would
  //               overflow the viewport's right edge.
  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!tooltip || !el) return;
    const PAD = 12;
    const w   = el.offsetWidth;
    const h   = el.offsetHeight;
    const vw  = window.innerWidth;
    const vh  = window.innerHeight;

    // Horizontal: prefer right, flip left if overflow
    let left = tooltip.x + PAD;
    if (left + w > vw - PAD) left = tooltip.x - PAD - w;

    // Vertical: prefer above cursor, drop below if too close to top
    let top = tooltip.y - h - 8;
    if (top < PAD) top = tooltip.y + PAD;
    // Also clamp bottom
    if (top + h > vh - PAD) top = vh - h - PAD;

    el.style.left       = `${left}px`;
    el.style.top        = `${top}px`;
    el.style.visibility = 'visible';
  }, [tooltip]);

  // ── Render ───────────────────────────────────────────────────
  return (
    <div className={`dashboard${tab === 'timeline' ? ' dashboard--timeline' : ''}`} ref={rootRef}>

      {/* ── Tabs — "My Projects" (with the whose-projects dropdown) | Timeline ── */}
      <div className="dash-tabs" role="tablist">
        <div className="dash-tab-wrap" ref={peopleMenuRef}>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'projects'}
            aria-haspopup={tab === 'projects' ? 'menu' : undefined}
            aria-expanded={tab === 'projects' ? peopleOpen : undefined}
            className={`dash-tab dash-tab--projects${tab === 'projects' ? ' active' : ''}`}
            onClick={onProjectsTabClick}
          >
            {projectsTitle}
            <span className={`myproj-chevron${peopleOpen ? ' open' : ''}`} aria-hidden="true">
              <svg width="10" height="10" viewBox="0 0 10 10">
                <path d="M1 3 L5 7 L9 3 Z" fill="currentColor" />
              </svg>
            </span>
          </button>
          {peopleOpen && (
            <div className="myproj-people-menu" role="menu">
              {[['Reporters', 'reporter'], ['Operators', 'operator']].map(([title, role]) => {
                const list = people.filter(u => (u.roles || []).includes(role));
                return (
                  <div key={role} className="myproj-people-group">
                    <div className="myproj-people-title">{title}</div>
                    {list.length === 0 ? (
                      <div className="myproj-people-none">No {title.toLowerCase()}</div>
                    ) : list.map(u => (
                      <button
                        key={u.email}
                        type="button"
                        role="menuitem"
                        className={`myproj-people-item${viewUser?.email === u.email ? ' selected' : ''}`}
                        onClick={() => pickPerson(u)}
                      >
                        {u.name}
                      </button>
                    ))}
                  </div>
                );
              })}
              <div className="myproj-people-group">
                <button
                  type="button"
                  role="menuitem"
                  className={`myproj-people-item${viewUser?.unassigned ? ' selected' : ''}`}
                  onClick={pickUnassigned}
                >
                  Unassigned
                </button>
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'timeline'}
          className={`dash-tab${tab === 'timeline' ? ' active' : ''}`}
          onClick={() => { setTab('timeline'); setPeopleOpen(false); }}
        >
          Terra Scheduled Timeline
        </button>
      </div>

      {/* ── Tab 1: project cards for the viewed user ── */}
      {tab === 'projects' && (
      <div className="myproj-section">
        {!fullyLoaded ? (
          <div className="myproj-empty">Loading…</div>
        ) : myProjects.length === 0 ? (
          <div className="myproj-empty">
            {viewUser?.unassigned
              ? 'No unassigned projects'
              : viewUser ? `No projects assigned to ${viewUser.name}` : 'No projects assigned'}
          </div>
        ) : (
          <div className="myproj-cards">
            {myProjects.map(p => (
              <button
                key={p.id}
                type="button"
                className="myproj-card"
                onClick={() => p.clientId && navigate(`/client/${p.clientId}/object/${p.objectId}/inspection/${p.id}`)}
              >
                <span className="insp-status-badge myproj-badge" data-status={p.status}>{p.status}</span>
                <span className="myproj-card-title">
                  {/* Object ID opens the object page; the rest of the card opens the inspection. */}
                  <span
                    className="myproj-card-objlink"
                    title="Open object"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (p.clientId) navigate(`/client/${p.clientId}/object/${p.objectId}`);
                    }}
                  >
                    {p.objTag}
                  </span>: {p.code}
                </span>
                <span className="myproj-card-site">{p.site}</span>
                {p.period && <span className="myproj-card-site">{p.period}</span>}
                {p.due && <span className="myproj-card-due">{fmtDueRelative(p.due, today)}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      )}

      {/* ── Tab 2: timeline + its header, sized to the calendar width so the
          frame border wraps the calendar and the nav sits at its right edge
          (rather than the screen edge). Grows by whole weeks; empty space is
          left on the right when a further week doesn't fit. The nav bar and
          week/day header stay frozen; only the object rows scroll. ── */}
      {tab === 'timeline' && (
      <div className="tl-wrap" style={{ width: totalW + FRAME_CHROME, maxWidth: 'calc(100% - 60px)' }}>

      {/* ── Page header (nav) — the tab carries the title ── */}
      <div className="dashboard-header">
        <div className="dashboard-nav">
          <button className="dash-nav-btn" onClick={() => setWeekOffset(w => w - 1)}>‹</button>
          <span className="dash-nav-label">
            W{weekHeaders[0].weekNum} – W{weekHeaders[weekHeaders.length - 1].weekNum}
          </span>
          <button className="dash-nav-btn" onClick={() => setWeekOffset(w => w + 1)}>›</button>
          <button className="dash-nav-today" onClick={() => setWeekOffset(0)}>Today</button>
          <button
            className="dash-nav-today"
            onClick={scrollToEarliest}
            disabled={filteredObjects.length === 0}
            title="Jump to the week of the earliest object start date"
          >Top</button>
        </div>
      </div>

      {/* ── Timeline frame ── */}
      <div className="tl-frame">
      <div className="tl-outer">
        <div className="tl-inner" style={{ width: totalW }}>

          {/* Vertical section-boundary overlays between each pair of weeks.
              2px lines spanning the full table height, below the bars. The
              header sits above the bars, so it draws its own copy (below). */}
          {weekLines}

          {/* ── Sticky two-row header ── */}
          <div className="tl-header">
            {weekLines}

            {/* Row 1: Week numbers + month */}
            <div className="tl-week-row">
              <div className="tl-week-spacer" style={{ width: LABEL_W, height: WEEK_HDR_H }} />
              {weekHeaders.map(({ off, weekNum, monthLabel }) => (
                <div
                  key={off}
                  className="tl-week-cell"
                  style={{ width: DAY_W * 7, height: WEEK_HDR_H }}
                >
                  W{weekNum}
                  <span className="tl-week-month">{monthLabel}</span>
                </div>
              ))}
            </div>

            {/* Row 2: Day headers — date number + abbreviation */}
            <div className="tl-day-row">
              <div
                className="tl-corner tl-corner--day"
                style={{ width: LABEL_W, height: DAY_HDR_H }}
              >
                Object
              </div>
              {dayHeaders.map(({ i, dateNum, dayAbbr, isWeekend, isToday }) => (
                <div
                  key={i}
                  className={[
                    'tl-day-hdr',
                    isWeekend ? 'tl-day-hdr--weekend' : '',
                  ].filter(Boolean).join(' ')}
                  style={{ width: DAY_W, height: DAY_HDR_H }}
                >
                  <span className={isToday ? 'tl-date-num tl-today-circle' : 'tl-date-num'}>
                    {dateNum}
                  </span>
                  <span className="tl-day-abbr">{dayAbbr}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Object rows ── */}
          {!objectsLoaded ? (
            /* Phase 1 — objects not fetched yet. */
            <div className="tl-empty tl-loading-text">Loading Timeline…</div>
          ) : filteredObjects.length === 0 ? (
            /* Phase 2 — objects fetched, genuinely none to show. */
            <div className="tl-empty">
              {objects.length === 0
                ? 'No objects found. Add objects with a start date to see the timeline.'
                : 'No objects have a start date set yet.'}
            </div>
          ) : !fullyLoaded ? (
            /* Phase 3 — objects in hand, inspections/clients still loading:
               real row labels with a full-width ghost bar (shimmer wave). */
            filteredObjects.map(obj => {
              const client   = clientMap[obj.clientId];
              const objTag   = obj.objectTag || obj.id;
              const siteName = client?.name || '';
              return (
                <div key={obj.id} className="tl-row" style={{ height: ROW_H }}>
                  <div
                    className="tl-row-label"
                    style={{ width: LABEL_W }}
                    title={`${objTag}${siteName ? ' — ' + siteName : ''}`}
                  >
                    <span className="tl-row-tag">{objTag}</span>
                    {siteName && <span className="tl-row-site">{siteName}</span>}
                  </div>
                  <div className="tl-row-canvas" style={{ width: VIEW_DAYS * DAY_W }}>
                    {dayHeaders.map(({ i, isWeekend }) => (
                      <div
                        key={i}
                        className={['tl-bg-cell', isWeekend ? 'tl-bg-cell--weekend' : ''].filter(Boolean).join(' ')}
                        style={{ width: DAY_W }}
                      />
                    ))}
                    <div className="tl-bar tl-bar-skeleton" style={{ left: 0, width: VIEW_DAYS * DAY_W }}>
                      <div className="tl-seg-skeleton" />
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            /* Phase 4 — fully loaded: the real timeline. */
            filteredObjects.map(obj => {
              const client   = clientMap[obj.clientId];
              const objTag   = obj.objectTag || obj.id;
              const siteName = client?.name || '';

              const rawInsps     = inspsByObject[obj.id] || [];
              // Fall back to '2 Weeks' for objects created before the default was added
              const deliveryDate = computeDeliveryDate(
                obj.generalInfo?.startDate,
                obj.settings?.reportingPeriod || '2 Weeks'
              );
              // Effective status is computed server-side; the bar just reads it.
              const computedInsps = rawInsps.map(insp => ({
                ...insp,
                eff: insp.effectiveStatus,
              }));
              const hasDelayed = computedInsps.some(i => i.eff === 'Delayed');
              // Every inspection delivered → collapsed single bar, unless the
              // user just clicked it open (expandedReady).
              const showReady = computedInsps.length > 0
                && computedInsps.every(i => i.eff === 'Delivery')
                && !expandedReady[obj.id];

              // ── Bar date range ─────────────────────────────────────────────
              // Start: always generalInfo.startDate
              // End:   deliveryDate (reportingPeriod offset from start)
              //        When "No Deadline" is set explicitly, skip the campaign
              //        endDate fallback and go straight to viewEndDate so segments
              //        always fill the visible window even when endDate is in the past.
              //        If no start date at all: row still shows, bar is absent.
              const isNoDeadline   = /no\s*deadline/i.test(obj.settings?.reportingPeriod || '');
              const startDate      = new Date(obj.generalInfo.startDate + 'T00:00:00');
              const endDate        = obj.generalInfo.endDate
                ? new Date(obj.generalInfo.endDate + 'T00:00:00')
                : null;
              const deliveryDateObj = deliveryDate
                ? new Date(deliveryDate + 'T00:00:00')
                : null;

              // "No Deadline" → always fill to the right edge of the visible window.
              // Otherwise: delivery date → campaign end date → visible window edge.
              // The ready bar has no end either — same as "No Deadline".
              const barEndDate = (isNoDeadline || showReady)
                ? viewEndDate
                : (deliveryDateObj || endDate || viewEndDate);

              // ── Compute bar position and segments ──────────────────────────
              // All variables initialised so the JSX below always has values.
              let barStartDay = 0;
              let barEndDay   = 0;
              let barLeft     = 0;
              let barWidth    = 0;
              let hasBar      = false;
              let segments    = [];

              if (barEndDate) {
                // When any inspection is delayed, extend the bar to at least today
                // so the overshoot past the deadline is visible on the timeline.
                const effectiveEnd = hasDelayed
                  ? new Date(Math.max(barEndDate.getTime(), today.getTime() + 86400000))
                  : barEndDate;

                barStartDay = clamp(dayOff(startDate),     0, VIEW_DAYS);
                barEndDay   = clamp(dayOff(effectiveEnd),  0, VIEW_DAYS);

                if (computedInsps.length === 0) {
                  segments = [{
                    key:     'empty',
                    flex:    1,
                    bg:      statusBg('Planning'),
                    label:   'No inspections yet',
                    onClick: () => navigate(`/client/${obj.clientId}/object/${obj.id}`),
                  }];
                } else {
                  // Each inspection gets its own equal-width slot.
                  // Same-status inspections keep individual slots — visible via border gaps.
                  segments = computedInsps.map(insp => ({
                    key:     insp.id,
                    flex:    1,
                    bg:      statusBg(insp.eff),
                    label:   `${insp.name || insp.type || 'Inspection'}: ${insp.eff}`,
                    sub:     `Assigned to: ${insp.assignedTo || 'No one'}`,
                    onClick: () =>
                      navigate(`/client/${obj.clientId}/object/${obj.id}/inspection/${insp.id}`),
                  }));
                }

                barLeft  = barStartDay * DAY_W;
                barWidth = Math.max(0, barEndDay - barStartDay) * DAY_W;
                hasBar   = barWidth > 0 && barStartDay < VIEW_DAYS && barEndDay > 0;
              }

              return (
                <div key={obj.id} className="tl-row" style={{ height: ROW_H }}>

                  {/* Sticky label — always rendered even when no bar */}
                  <div
                    className="tl-row-label"
                    style={{ width: LABEL_W }}
                    onClick={() => navigate(`/client/${obj.clientId}/object/${obj.id}`)}
                    title={`${objTag}${siteName ? ' — ' + siteName : ''}`}
                  >
                    <span className="tl-row-tag">{objTag}</span>
                    {siteName && <span className="tl-row-site">{siteName}</span>}
                  </div>

                  {/* Canvas: day bg cells + bar (bar optional) */}
                  <div className="tl-row-canvas" style={{ width: VIEW_DAYS * DAY_W }}>

                    {/* Day background cells (grid lines) */}
                    {dayHeaders.map(({ i, isWeekend }) => (
                      <div
                        key={i}
                        className={['tl-bg-cell', isWeekend ? 'tl-bg-cell--weekend' : ''].filter(Boolean).join(' ')}
                        style={{ width: DAY_W }}
                      />
                    ))}

                    {/* Bar: startDate → deliveryDate, split into one segment per inspection */}
                    {hasBar && showReady && (
                      <div className="tl-bar" style={{ left: barLeft, width: barWidth }}>
                        <div
                          className="tl-seg tl-seg--ready"
                          style={{ flex: 1, background: statusBg('Delivery') }}
                          onClick={() => expandReadyRow(obj)}
                        >
                          <span className="tl-seg-text">
                            {obj.generalInfo?.involvementL3Inspector ? READY_TEXT_L3 : READY_TEXT_CLIENT}
                          </span>
                        </div>
                      </div>
                    )}
                    {hasBar && !showReady && (
                      <div className="tl-bar" style={{ left: barLeft, width: barWidth }}>
                        {segments.map(seg => (
                          <div
                            key={seg.key}
                            className="tl-seg"
                            style={{ flex: seg.flex, background: seg.bg }}
                            onClick={seg.onClick}
                            onMouseEnter={e => onSegEnter(e, seg.label, seg.sub)}
                            onMouseMove={onSegMove}
                            onMouseLeave={onSegLeave}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}

        </div>
      </div>
      </div>{/* end tl-frame */}
      </div>
      )}{/* end tl-wrap */}

      {/* ── Floating tooltip — positioned by useLayoutEffect ── */}
      {tooltip && (
        <div
          ref={tooltipRef}
          className="tl-tooltip"
          style={{ visibility: 'hidden', left: 0, top: 0 }}
        >
          <div>{tooltip.text}</div>
          {tooltip.sub && <div className="tl-tooltip-sub">{tooltip.sub}</div>}
        </div>
      )}

    </div>
  );
}
